import { useState } from 'react';
import { deepObjectEquals } from 'utils/object';

/**
 * Hook to manage data source parameter changes, particularly syncing
 * defaultFilter changes into the current filter state.
 *
 * @param defaultFilter The default filter provided to the data source
 * @param setFilter The state setter function for the filter
 */
// Uses React's derived-state pattern (setState during render) so the filter
// is updated synchronously rather than one render later via useEffect.
// On the first render prevDefaultFilter === defaultFilter by construction, so
// no update is triggered and there's no need to skip the first render as you'd
// need in a use effect impl.
// See: https://react.dev/reference/react/useState#storing-information-from-previous-renders
export function useDataSourceParams<TFilter extends object>(
    defaultFilter: TFilter,
    setFilter: (filter: TFilter) => void,
): void {
    const [prevDefaultFilter, setPrevDefaultFilter] = useState(defaultFilter);

    if (prevDefaultFilter !== defaultFilter) {
        const keys = Object.keys(defaultFilter) as (keyof TFilter)[];
        const hasChanged =
            Object.keys(prevDefaultFilter).length !== keys.length ||
            keys.some(
                (key) => !deepObjectEquals(prevDefaultFilter[key], defaultFilter[key], 3),
            );

        if (hasChanged) {
            setPrevDefaultFilter(defaultFilter);
            setFilter(defaultFilter);
        }
    }
}
